import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, getToken, setToken, setUnlockToken, getUnlockToken, setAuthListeners } from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [unlocked, setUnlocked] = useState(false);
  const [blurred, setBlurred] = useState(false);
  const unlockedRef = useRef(false);
  unlockedRef.current = unlocked;

  const lock = useCallback(async (remote = true) => {
    const was = unlockedRef.current;
    setUnlockToken(null);
    setUnlocked(false);
    if (was && remote && getToken()) {
      try { await api.post("/pin/lock"); } catch (_) {}
    }
  }, []);

  const logout = useCallback(async () => {
    if (getToken()) {
      try { await api.post("/auth/logout"); } catch (_) {}
    }
    setToken(null);
    setUnlockToken(null);
    setUnlocked(false);
    setUser(null);
  }, []);

  const loginSuccess = useCallback((data) => {
    setToken(data.token);
    setUnlockToken(null);
    setUnlocked(false);
    setUser(data.user);
  }, []);

  const unlockSuccess = useCallback((data) => {
    setUnlockToken(data.unlock_token);
    setUnlocked(true);
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  useEffect(() => {
    setAuthListeners({
      onUnauthorized: () => { setToken(null); setUnlockToken(null); setUnlocked(false); setUser(null); },
      onPinRequired: () => { setUnlockToken(null); setUnlocked(false); },
    });
    if (!getToken()) { setUser(null); return; }
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => { setToken(null); setUser(null); });
  }, []);

  // Auto-lock por inactividad + bloqueo inmediato al ocultar/perder foco
  useEffect(() => {
    if (!user) return;
    const seconds = user.settings?.auto_bloqueo_segundos || 60;
    let timer;
    const arm = () => {
      clearTimeout(timer);
      if (unlockedRef.current) timer = setTimeout(() => lock(), seconds * 1000);
    };
    const hide = () => { setBlurred(true); if (unlockedRef.current) lock(); };
    const show = () => setBlurred(false);
    const onBlur = () => setTimeout(() => { if (!document.hasFocus()) setBlurred(true); }, 0);
    const onVisibility = () => (document.visibilityState === "hidden" ? hide() : show());
    const onActivity = () => { arm(); show(); };
    ["pointerdown", "keydown", "touchstart"].forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    window.addEventListener("mousemove", arm, { passive: true });
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", show);
    window.addEventListener("focusin", show);
    window.addEventListener("pagehide", hide);
    document.addEventListener("visibilitychange", onVisibility);
    arm();
    return () => {
      clearTimeout(timer);
      ["pointerdown", "keydown", "touchstart"].forEach((e) => window.removeEventListener(e, onActivity));
      window.removeEventListener("mousemove", arm);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", show);
      window.removeEventListener("focusin", show);
      window.removeEventListener("pagehide", hide);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [user, unlocked, lock]);

  const value = useMemo(() => ({
    user, unlocked: unlocked && !!getUnlockToken(), blurred, setBlurred,
    lock, logout, loginSuccess, unlockSuccess, refreshUser, setUser,
  }), [user, unlocked, blurred, lock, logout, loginSuccess, unlockSuccess, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
